import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, brl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Money, HideValuesToggle } from "@/components/HideValues";
import { AskAssistant } from "@/components/AskAssistant";
import { Loader2, Calendar, Wallet, Users as UsersIcon, ClipboardList, Building2, PieChart, Megaphone, UserX, UserCheck, Star, Filter, Boxes, AlertTriangle, ArrowDownCircle, ArrowUpCircle, TrendingUp, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useSatisfaction } from "@/pages/OwnerSatisfaction";
import { TopCards, GoalBar, PanelCharts, usePanel } from "@/components/PanelCards";
import { CustomerMixChart, SourceDonutChart, MechanicPerformance, Section } from "@/components/PanelExtras";

function SatisfactionKpi() {
  const [d] = useSatisfaction({});
  if (!d || !d.count) return null;
  return (
    <Link to="/owner/satisfacao" data-testid="kpi-satisfaction" className="flex items-center gap-4 rounded-2xl border border-card2-border bg-card2 p-4">
      <div className="font-display text-4xl font-black text-zinc-100">{d.avg}</div>
      <div><div className="text-amber-400">{"★".repeat(Math.round(d.avg))}</div><div className="text-xs text-zinc-500">Nota média de satisfação · {d.count} avaliação(ões){d.low_pending ? ` · ${d.low_pending} nota(s) baixa(s) a resolver` : ""}</div></div>
    </Link>
  );
}

const PAY_LBL = { dinheiro: "Dinheiro", pix: "Pix", debito: "Cartão de débito", credito: "Cartão de crédito", boleto: "Boleto", financiamento: "Financiamento" };


function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
function monthStartIso() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; }

const RANGES = [
  { key: "today", label: "Hoje", range: () => [todayIso(), todayIso()] },
  { key: "7d", label: "7 dias", range: () => [daysAgoIso(6), todayIso()] },
  { key: "month", label: "Mês", range: () => [monthStartIso(), todayIso()] },
];

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="h-full rounded-2xl lg:rounded-xl border border-card2-border bg-card2 p-5 lg:p-3 flex items-center gap-4 lg:gap-3">
      <div className="h-12 w-12 lg:h-8 lg:w-8 shrink-0 rounded-xl lg:rounded-lg flex items-center justify-center" style={{ background: "rgb(var(--c-brand-rgb)/0.15)", color: "var(--c-brand-neon)" }}>
        <Icon className="h-6 w-6 lg:h-4 lg:w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs lg:text-[11px] font-mono-caps text-zinc-500 truncate">{label}</div>
        <div className="font-display text-2xl lg:text-lg font-black uppercase truncate text-zinc-100">{value}</div>
      </div>
    </div>
  );
}

export default function OwnerKpi() {
  const { user } = useAuth();
  const canToggleScope = (user?.shops?.length ?? 0) > 1;
  const [preset, setPreset] = useState("month");
  const [[from, to], setRange] = useState(() => RANGES.find((r) => r.key === "month").range());
  const [scope, setScope] = useState(user?.shop_id === "all" ? "all" : "current");
  const [data, setData] = useState(null);
  const [panel, reloadPanel] = usePanel();
  const [loading, setLoading] = useState(false);

  const [perf, setPerf] = useState([]);
  useEffect(() => { api.get("/mechanics/performance").then(({ data }) => setPerf(data)).catch(() => setPerf([])); }, [scope]);

  const applyPreset = (key) => {
    setPreset(key);
    const r = RANGES.find((x) => x.key === key);
    if (r) setRange(r.range());
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/dashboard`, { params: { from, to, scope } });
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [from, to, scope]);

  const title = useMemo(() => {
    if (scope === "all") return "Todas as unidades";
    const s = user?.shops?.find((x) => x.id === user.shop_id);
    return s?.name || user?.shop?.name || "";
  }, [scope, user]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-black uppercase text-zinc-100">Painel</h1>
          <p className="text-sm text-zinc-400 mt-1">
            Faturamento, clientes e ordens no período — {title}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap"><AskAssistant /><HideValuesToggle /></div>
      </div>
      <Section title="Origem dos clientes" testid="section-sources">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <CustomerMixChart mix={data?.customer_mix} />
          <SourceDonutChart bySource={data?.by_source} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Kpi testid="kpi-active" icon={UserCheck} label="Clientes ativos" value={loading ? "…" : (data?.active_customers ?? 0)} />
          <Kpi testid="kpi-lost" icon={UserX} label="Clientes sumidos" value={loading ? "…" : (data?.lost_customers ?? 0)} />
        </div>
      </Section>

      <div data-testid="dashboard-filters" className="rounded-2xl border border-card2-border bg-appbg-soft p-4 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1 flex-wrap">
          {RANGES.map((r) => (
            <Button key={r.key} data-testid={`range-${r.key}`} variant={preset === r.key ? "default" : "outline"}
                    onClick={() => applyPreset(r.key)}
                    className={preset === r.key ? "bg-amber-brand hover:bg-amber-brandDark text-black font-bold" : "bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800"}>
              {r.label}
            </Button>
          ))}
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">De</Label>
          <Input data-testid="range-from" type="date" value={from} onChange={(e) => { setPreset("custom"); setRange(([_, t]) => [e.target.value, t]); }}
                 className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Até</Label>
          <Input data-testid="range-to" type="date" value={to} onChange={(e) => { setPreset("custom"); setRange(([f]) => [f, e.target.value]); }}
                 className="mt-1 bg-zinc-900 border-zinc-800" />
        </div>
        {canToggleScope && (
          <div className="flex items-center gap-1 ml-auto">
            <Button data-testid="scope-current" variant={scope === "current" ? "default" : "outline"} onClick={() => setScope("current")}
                    className={scope === "current" ? "bg-amber-brand hover:bg-amber-brandDark text-black font-bold" : "bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800"}>
              Esta unidade
            </Button>
            <Button data-testid="scope-all" variant={scope === "all" ? "default" : "outline"} onClick={() => setScope("all")}
                    className={scope === "all" ? "bg-amber-brand hover:bg-amber-brandDark text-black font-bold" : "bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800"}>
              Todas as unidades
            </Button>
          </div>
        )}
      </div>

      <Section title="Ordens de serviço" testid="section-orders">
        <TopCards d={panel} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi testid="kpi-orders" icon={ClipboardList} label="Ordens no período" value={loading ? "…" : (data?.orders ?? 0)} />
          <Kpi testid="kpi-stale" icon={Filter} label="Orçamentos parados" value={loading ? "…" : (data?.stale_budgets ?? 0)} />
          <Kpi testid="kpi-approval" icon={PieChart} label="Taxa de aprovação" value={loading ? "…" : `${Math.round((data?.approval_rate || 0) * 100)}%`} />
          <Kpi testid="kpi-reviews" icon={Star} label="Avaliações pedidas" value={loading ? "…" : (data?.reviews_requested ?? 0)} />
        </div>
      </Section>

      <Section title="Faturamento e meta" testid="section-revenue">
        <GoalBar d={panel} onSaved={reloadPanel} />
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Kpi testid="kpi-revenue" icon={Wallet} label="Faturamento (OS aprovadas)" value={loading ? "…" : <Money value={data?.revenue ?? 0} />} />
          <Kpi testid="kpi-profit" icon={TrendingUp} label="Lucro do período" value={loading ? "…" : <Money value={data?.profit ?? 0} />} />
          <Kpi testid="kpi-sales" icon={ShoppingCart} label={`Vendas balcão (${data?.sales_count ?? 0})`} value={loading ? "…" : <Money value={data?.sales_revenue ?? 0} />} />
          <Kpi testid="kpi-receivable-7d" icon={ArrowDownCircle} label="A receber 7 dias" value={loading ? "…" : <Money value={data?.receivable_7d ?? 0} />} />
          <Kpi testid="kpi-payable-7d" icon={ArrowUpCircle} label="A pagar 7 dias" value={loading ? "…" : <Money value={data?.payable_7d ?? 0} />} />
          <Kpi testid="kpi-customers" icon={UsersIcon} label="Clientes atendidos" value={loading ? "…" : (data?.customers ?? 0)} />
        </div>
        <PanelCharts d={panel} />
      </Section>

      <SatisfactionKpi />

      <Section title="Estoque e compras" testid="section-stock">
        <div className="grid grid-cols-2 gap-3">
          <Kpi testid="kpi-stock-value" icon={Boxes} label="Valor em estoque" value={loading ? "…" : <Money value={data?.stock_value ?? 0} />} />
          <Kpi testid="kpi-low-stock" icon={AlertTriangle} label="Abaixo do mínimo" value={loading ? "…" : (data?.low_stock ?? 0)} />
        </div>
      </Section>

      <Section title="Mecânicos e consultores" testid="section-team">
        <MechanicPerformance rows={perf} />
        <div className="flex flex-wrap gap-4 pt-1">
          <Link to="/owner/equipe" data-testid="section-team-report-link" className="inline-block text-sm text-green-300 hover:underline">Ver relatório completo da equipe →</Link>
          <Link to="/owner/relatorios/resumo" data-testid="section-report-summary-link" className="inline-block text-sm text-emerald-300 hover:underline">Resumo Geral →</Link>
        </div>
      </Section>

      {data?.by_shop && data.by_shop.length > 1 && (
        <div data-testid="kpi-by-shop" className="rounded-2xl border border-card2-border bg-appbg-soft p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-mono-caps text-zinc-500"><Building2 className="h-4 w-4" /> Por unidade</div>
          <div className="divide-y divide-zinc-900">
            {data.by_shop.map((s) => (
              <div key={s.shop_id} data-testid={`by-shop-${s.shop_id}`} className="flex items-center justify-between py-2 text-sm">
                <span className="text-zinc-200 font-semibold">{s.shop_name}</span>
                <span className="text-zinc-400">{s.orders} OS · {brl(s.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> carregando…</div>}

      {data?.by_payment && data.by_payment.length > 0 && (
        <div id="pagamentos" data-testid="kpi-by-payment" className="rounded-2xl border border-card2-border bg-appbg-soft p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-mono-caps text-zinc-500"><PieChart className="h-4 w-4" /> Faturamento por forma de pagamento</div>
          <div className="divide-y divide-zinc-900">
            {data.by_payment.map((p) => (
              <div key={p.method} data-testid={`by-payment-${p.method}`} className="flex items-center justify-between py-2 text-sm">
                <span className="text-zinc-200 font-semibold">{PAY_LBL[p.method] || p.method}</span>
                <span className="text-zinc-400">{brl(p.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
